import { useEffect } from "react";

/**
 * Keeps field errors in sync with their values after the first submit attempt:
 * an error clears the moment its field becomes valid, and comes back if the
 * field is emptied or made invalid again.
 *
 * Errors are re-derived from the same validation function the submit handler
 * uses, so live feedback can never drift from the rules that produced them.
 * Only keys listed in `managedKeys` are touched — errors owned by other flows
 * (banner upload checks, API responses) are left alone.
 *
 * @param {Function} setErrors      state setter holding the error object
 * @param {Function} computeErrors  () => errors object, or null before the first
 *                                  submit attempt so the form stays quiet while
 *                                  the user is still filling it in
 * @param {string[]} managedKeys    error keys this hook owns
 * @param {Array}    deps           the field values to watch
 */
export const useLiveFieldErrors = ({ setErrors, computeErrors, managedKeys = [], deps = [] }) => {
    useEffect(() => {
        setErrors(prev => {
            const fresh = computeErrors();
            if (!fresh) return prev; // nothing validated yet — do not nag early

            const previous = prev && typeof prev === "object" ? prev : {};
            const next = {};

            // errors this hook does not own stay exactly as they were
            Object.keys(previous).forEach(key => {
                if (previous[key] && !managedKeys.includes(key)) {
                    next[key] = previous[key];
                }
            });

            managedKeys.forEach(key => {
                if (fresh[key]) next[key] = fresh[key];
            });

            const previousKeys = Object.keys(previous).filter(key => previous[key]);
            const nextKeys = Object.keys(next);
            const unchanged =
                previousKeys.length === nextKeys.length &&
                nextKeys.every(key => previous[key] === next[key]);

            // same object back when nothing moved, so React skips the re-render
            return unchanged ? prev : next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};

export default useLiveFieldErrors;
